import { useEffect, useId, useRef, useState } from "react";
import { InfoIcon } from "lucide-react";
import type { ModelSpendStatus } from "../../state/modelSpend";
import { formatGbp } from "../../usage/usageFormat";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Tooltip, TooltipPopup, TooltipTrigger } from "../ui/tooltip";

export function parsePaidLimit(value: string): number | null {
  if (!/^\d+(?:\.\d{1,2})?$/.test(value.trim())) return null;
  const amount = Number(value);
  return Number.isFinite(amount) && amount >= 0 && amount <= 1_000_000 ? amount : null;
}

export function PaidUsageLimitForm({
  status,
  onSave,
}: {
  status: ModelSpendStatus;
  onSave: (amount: number) => Promise<string | null>;
}) {
  const id = useId();
  const changeButton = useRef<HTMLButtonElement>(null);
  const wasEditing = useRef(false);
  const [editing, setEditing] = useState(false);
  const [value, setValue] = useState(String(status.paidLimitGbp ?? 0));
  const [savedLimit, setSavedLimit] = useState(status.paidLimitGbp ?? 0);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState<string | null>(null);
  useEffect(() => {
    setSavedLimit(status.paidLimitGbp ?? 0);
    setValue(String(status.paidLimitGbp ?? 0));
  }, [status.paidLimitGbp]);
  useEffect(() => {
    if (wasEditing.current && !editing) changeButton.current?.focus();
    wasEditing.current = editing;
  }, [editing]);
  const amount = parsePaidLimit(value);
  const reset = new Date(`${status.resetsOn}T00:00:00Z`);
  const resetDate = Number.isFinite(reset.getTime())
    ? new Intl.DateTimeFormat("en-GB", { day: "numeric", month: "short", timeZone: "UTC" }).format(
        reset,
      )
    : status.resetsOn;
  return (
    <section aria-label="Personal usage limit" className="py-1">
      <div className="flex items-center gap-1.5">
        <h3 className="text-sm font-medium">Monthly chat usage</h3>
        <Tooltip>
          <TooltipTrigger
            render={
              <button
                type="button"
                aria-label="How usage limits work"
                className="inline-flex size-6 items-center justify-center rounded-md text-muted-foreground/70 hover:text-foreground focus-visible:outline-2 focus-visible:outline-ring"
              />
            }
          >
            <InfoIcon className="size-3.5" strokeWidth={1.5} />
          </TooltipTrigger>
          <TooltipPopup side="top" className="max-w-xs leading-relaxed">
            Your limit covers paid usage beyond the included allowance, in this workspace only. New
            chat turns stop at the limit; work already running can take spending above it. Ultrafast is
            charged at cost. Other tiers use your workspace’s {status.overageMultiplier}× rate.
          </TooltipPopup>
        </Tooltip>
      </div>
      <p className="mt-0.5 text-xs text-muted-foreground">
        Your personal allowance in this workspace · Resets {resetDate}
      </p>
      <dl className="mt-4 grid grid-cols-3 gap-4">
        <div>
          <dt className="text-xs text-muted-foreground">Included allowance</dt>
          <dd className="mt-1 text-lg font-medium tabular-nums">
            {formatGbp(status.includedGbp ?? 0)}
          </dd>
        </div>
        <div>
          <dt className="text-xs text-muted-foreground">Included used</dt>
          <dd className="mt-1 text-lg font-medium tabular-nums">
            {formatGbp(Math.min(status.spentGbp, status.includedGbp ?? 0))}
          </dd>
        </div>
        <div>
          <dt className="text-xs text-muted-foreground">Paid usage</dt>
          <dd className="mt-1 text-lg font-medium tabular-nums">
            {formatGbp(status.overageChargeGbp)}
          </dd>
        </div>
      </dl>
      <div className="mt-4 border-t border-border/60 pt-4">
        {editing ? (
          <form
            onSubmit={async (event) => {
              event.preventDefault();
              if (amount === null || saving) return;
              setSaving(true);
              setMessage(null);
              try {
                const error = await onSave(amount);
                if (error) setMessage(error);
                else {
                  setSavedLimit(amount);
                  setEditing(false);
                  setMessage("Limit updated.");
                }
              } catch {
                setMessage("Could not save your limit. Please try again.");
              } finally {
                setSaving(false);
              }
            }}
          >
            <div className="flex flex-wrap items-center justify-between gap-3">
              <label htmlFor={id} className="text-sm">
                Paid usage limit
              </label>
              <div className="flex items-center gap-2">
                <div className="relative">
                  <span
                    aria-hidden="true"
                    className="pointer-events-none absolute inset-y-0 left-2.5 z-10 flex items-center text-sm text-muted-foreground"
                  >
                    £
                  </span>
                  <Input
                    id={id}
                    autoFocus
                    inputMode="decimal"
                    value={value}
                    disabled={saving}
                    aria-describedby={`${id}-help${amount === null ? ` ${id}-error` : ""}`}
                    aria-invalid={amount === null}
                    onChange={(event) => {
                      setValue(event.target.value);
                      setMessage(null);
                    }}
                    className="h-8 w-24 pl-6 text-sm tabular-nums"
                  />
                </div>
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  disabled={saving}
                  onClick={() => {
                    setEditing(false);
                    setValue(String(savedLimit));
                    setMessage(null);
                  }}
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  size="sm"
                  disabled={saving || amount === null || amount === savedLimit}
                >
                  {saving ? "Saving…" : "Save"}
                </Button>
              </div>
            </div>
            <p id={`${id}-help`} className="mt-2 text-xs text-muted-foreground">
              This is the most you allow in paid chat charges each month, beyond your included
              allowance. Set £0 to turn off paid usage.
            </p>
            {amount === null && (
              <p id={`${id}-error`} className="mt-2 text-xs text-destructive">
                Enter £0–£1,000,000, with up to two decimal places.
              </p>
            )}
          </form>
        ) : (
          <div className="flex items-center justify-between gap-3">
            <div>
              <p className="text-sm">
                Monthly paid limit{" "}
                <span className="ml-2 inline-block whitespace-nowrap font-medium tabular-nums">
                  {formatGbp(savedLimit)}
                  <span className="font-normal text-muted-foreground"> / month</span>
                </span>
              </p>
              <p className="mt-1 text-xs text-muted-foreground">
                {savedLimit === 0 && status.overageChargeGbp === 0
                  ? "Chat pauses when your included allowance is used."
                  : `Up to ${formatGbp(savedLimit)} of paid chat usage after your included allowance.`}
              </p>
            </div>
            <Button
              ref={changeButton}
              type="button"
              variant="outline"
              size="sm"
              onClick={() => {
                setEditing(true);
                setValue(String(savedLimit));
                setMessage(null);
              }}
            >
              Edit limit
            </Button>
          </div>
        )}
        {!status.onDemandEnabled && (
          <p className="mt-2 text-xs text-muted-foreground">
            Paid usage is disabled by your organization. Your limit applies only when it is enabled.
          </p>
        )}
        <p className="mt-3 text-xs text-muted-foreground">
          Inspector runs independently and does not use your personal allowance.
        </p>
        {status.state === "blocked" && !editing && (
          <p className="mt-2 text-xs text-destructive">
            {status.onDemandEnabled
              ? "Your chat limit is reached. Increase your paid limit or wait for the monthly reset."
              : "Your organization has disabled paid chat usage."}
          </p>
        )}
        {message && (
          <p role={editing ? "alert" : "status"} className="mt-2 text-xs text-muted-foreground">
            {message}
          </p>
        )}
      </div>
    </section>
  );
}
